import styled from '@emotion/styled';
import { Icon, IconNames, rem } from '@triumph/design-system';
import { Dropdown } from 'primereact/dropdown';
import { useState } from 'react';

import {
    FILE_TYPE_DROPDOWN_PANEL_CLASS,
    FileTypeDropdownPanelStyles,
} from '@/components/shared/components/FileTypeDropdownPanel';
import { FILE_TYPE_OPTION_GROUPS, getFileTypeLabel } from '@/components/shared/utils/utils';

import { Document, FileType } from '../../Applicant.types';

interface EditableDocumentTypeCellProps {
    file: Document;
    onSaveDocumentType: (fileId: string, newType: FileType) => void;
    hasEditPermission: boolean;
}

const DocumentTypeCell = styled.div`
    display: flex;
    align-items: center;
    gap: ${rem(8)};
    width: 100%;
    min-width: 0;
`;

const ActionIconsWrapper = styled.div`
    display: flex;
    gap: ${rem(4)};
    align-items: center;
`;

const IconButton = styled.button`
    border: none;
    background: none;
    padding: ${rem(4)};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.8;
    }

    &.edit-icon-button {
        svg {
            color: #0ea5e9;
        }

        &:hover svg {
            color: #0284c7;
        }
    }
`;

const EditIcon = styled(Icon)`
    color: inherit;
`;

const SaveIcon = styled(Icon)`
    color: #22c55e;
`;

const CancelIcon = styled(Icon)`
    color: #ef4444;
`;

const TypeLabel = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const EditableDocumentTypeCell = ({
    file,
    onSaveDocumentType,
    hasEditPermission,
}: EditableDocumentTypeCellProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedType, setEditedType] = useState<FileType | null>(null);

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const currentType = FileType[file.documentType as keyof typeof FileType] ?? FileType.Other;
        setEditedType(currentType);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedType(null);
    };

    const handleSaveDocumentType = () => {
        if (editedType == null) {
            return;
        }

        onSaveDocumentType(file.fileId, editedType);
        setIsEditing(false);
        setEditedType(null);
    };

    if (isEditing) {
        return (
            <DocumentTypeCell>
                <FileTypeDropdownPanelStyles />
                <Dropdown
                    data-testid={`edit-document-type-${file.fileId}`}
                    value={editedType}
                    options={FILE_TYPE_OPTION_GROUPS}
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    panelClassName={FILE_TYPE_DROPDOWN_PANEL_CLASS}
                    onChange={(e) => setEditedType(e.value)}
                    filter
                    filterPlaceholder="Search"
                    autoFocus
                    style={{ flex: 1, minWidth: 0 }}
                />
                <ActionIconsWrapper>
                    <IconButton
                        onClick={handleSaveDocumentType}
                        title="Save"
                        type="button"
                        data-testid="save-document-type-btn"
                    >
                        <SaveIcon
                            name={IconNames.Interface_Checkmark}
                            id={`save-document-type-${file.fileId}`}
                            size={18}
                            altText="Save"
                        />
                    </IconButton>
                    <IconButton
                        onClick={handleCancelEdit}
                        title="Cancel"
                        type="button"
                        data-testid="cancel-document-type-btn"
                    >
                        <CancelIcon
                            name={IconNames.Danger_CloseX}
                            id={`cancel-document-type-${file.fileId}`}
                            size={18}
                            altText="Cancel"
                        />
                    </IconButton>
                </ActionIconsWrapper>
            </DocumentTypeCell>
        );
    }

    return (
        <DocumentTypeCell>
            <TypeLabel>{getFileTypeLabel(file.documentType)}</TypeLabel>
            {hasEditPermission ? (
                <IconButton
                    onClick={handleEditClick}
                    title="Edit document type"
                    type="button"
                    className="edit-icon-button"
                    data-testid="edit-document-type-icon"
                >
                    <EditIcon
                        name={IconNames.Interface_Pencil}
                        id={`edit-document-type-icon-${file.fileId}`}
                        size={16}
                        altText="Edit document type"
                    />
                </IconButton>
            ) : null}
        </DocumentTypeCell>
    );
};
